import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronDown, Menu, MessageCircle, Globe, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Logo } from "@/components/Logo";
import { NavLink, Link, useLang } from "@/i18n/routing";
import { LANGS, LANG_LABELS, LANG_NAMES } from "@/i18n";
import { NAV, CONTACT, waLink } from "@/data/site";

const linkBase = "relative px-3 py-2 text-sm font-semibold transition-colors";

export const Header = () => {
  const { t } = useTranslation();
  const lang = useLang();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const switchLang = (l) => {
    localStorage.setItem("vc_lang", l);
    navigate(location.pathname.replace(/^\/[^/]+/, `/${l}`) + location.search);
  };

  return (
    <header className="sticky top-0 z-50 border-b border-[hsl(var(--vc-line))] bg-white/90 backdrop-blur-md">
      <div className="vc-container flex h-[68px] items-center justify-between gap-4">
        <Logo />

        {/* Desktop nav */}
        <nav className="hidden items-center lg:flex" aria-label="Main">
          {NAV.map((item) =>
            item.children ? (
              <div key={item.label} className="group relative">
                <NavLink
                  to={item.to}
                  data-testid={`nav-${item.tKey}`}
                  className={({ isActive }) =>
                    `${linkBase} inline-flex items-center gap-1 ${isActive || location.pathname.startsWith(`/${lang}${item.to}`) ? "text-brand" : "text-[hsl(var(--vc-ink))] hover:text-brand"}`
                  }
                >
                  {t(`nav.${item.tKey}`)}
                  <ChevronDown className="h-3.5 w-3.5 transition-transform duration-200 group-hover:rotate-180" />
                </NavLink>
                <div className="invisible absolute left-1/2 top-full z-50 w-64 -translate-x-1/2 translate-y-1 pt-2 opacity-0 transition-all duration-200 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
                  <div className="overflow-hidden rounded-xl border border-[hsl(var(--vc-line))] bg-white p-1.5 vc-shadow-card">
                    {item.children.map((c) => (
                      <NavLink
                        key={c.to}
                        to={c.to}
                        data-testid={`dropdown-${c.to.split("/").pop()}`}
                        className={({ isActive }) =>
                          `block rounded-lg px-3 py-2 text-sm font-medium transition-colors ${isActive ? "bg-mint text-brand" : "text-[hsl(var(--vc-slate))] hover:bg-mint hover:text-brand"}`
                        }
                      >
                        {t(`nav.${c.tKey}`)}
                      </NavLink>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <NavLink
                key={item.label}
                to={item.to}
                end={item.to === "/"}
                data-testid={`nav-${item.tKey}`}
                className={({ isActive }) =>
                  `${linkBase} ${isActive ? "text-brand" : "text-[hsl(var(--vc-ink))] hover:text-brand"}`
                }
              >
                {t(`nav.${item.tKey}`)}
              </NavLink>
            )
          )}
        </nav>

        <div className="flex items-center gap-2">
          {/* Language selector */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="lang-trigger" className="hidden items-center gap-1 rounded-lg px-2 py-1.5 text-sm font-semibold text-[hsl(var(--vc-ink))] hover:bg-mint sm:inline-flex">
                <Globe className="h-4 w-4 text-brand" /> {LANG_LABELS[lang]}
                <ChevronDown className="h-3.5 w-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {LANGS.map((l) => (
                <DropdownMenuItem key={l} onClick={() => switchLang(l)} data-testid={`lang-${l}`} className="flex items-center justify-between gap-4">
                  <span>{LANG_NAMES[l]}</span>
                  {l === lang && <Check className="h-4 w-4 text-brand" />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* WhatsApp phone */}
          <Button asChild className="hidden bg-brand font-bold text-white hover:opacity-90 sm:inline-flex" data-testid="header-whatsapp-btn">
            <a href={waLink()} target="_blank" rel="noopener noreferrer">
              <MessageCircle className="mr-2 h-4 w-4" /> {CONTACT.whatsappDisplay}
            </a>
          </Button>

          {/* Mobile menu */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button className="grid h-10 w-10 place-items-center rounded-lg border border-[hsl(var(--vc-line))] text-[hsl(var(--vc-ink))] lg:hidden" data-testid="mobile-menu-btn" aria-label="Menu">
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[88vw] max-w-sm overflow-y-auto p-0">
              <div className="border-b border-[hsl(var(--vc-line))] p-5"><Logo /></div>
              <nav className="flex flex-col gap-1 p-4" aria-label="Mobile">
                {NAV.map((item) =>
                  item.children ? (
                    <Accordion key={item.label} type="single" collapsible>
                      <AccordionItem value={item.label} className="border-0">
                        <AccordionTrigger className="rounded-lg px-3 py-2.5 text-base font-semibold text-[hsl(var(--vc-ink))] hover:bg-mint hover:no-underline" data-testid={`m-nav-${item.tKey}`}>
                          {t(`nav.${item.tKey}`)}
                        </AccordionTrigger>
                        <AccordionContent className="pb-1 pl-3">
                          <SheetClose asChild>
                            <Link to={item.to} className="block rounded-lg px-3 py-2 text-sm font-semibold text-brand">{t("nav.all")} {t(`nav.${item.tKey}`)}</Link>
                          </SheetClose>
                          {item.children.map((c) => (
                            <SheetClose asChild key={c.to}>
                              <Link to={c.to} data-testid={`m-dropdown-${c.to.split("/").pop()}`} className="block rounded-lg px-3 py-2 text-sm text-[hsl(var(--vc-slate))] hover:bg-mint hover:text-brand">{t(`nav.${c.tKey}`)}</Link>
                            </SheetClose>
                          ))}
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  ) : (
                    <SheetClose asChild key={item.label}>
                      <Link to={item.to} data-testid={`m-nav-${item.tKey}`} className="rounded-lg px-3 py-2.5 text-base font-semibold text-[hsl(var(--vc-ink))] hover:bg-mint hover:text-brand">{t(`nav.${item.tKey}`)}</Link>
                    </SheetClose>
                  )
                )}
                <div className="mt-4 flex flex-wrap gap-2 border-t border-[hsl(var(--vc-line))] pt-4">
                  {LANGS.map((l) => (
                    <button key={l} onClick={() => { switchLang(l); setMobileOpen(false); }} data-testid={`m-lang-${l}`}
                      className={`rounded-lg px-3 py-1.5 text-sm font-semibold ${l === lang ? "bg-brand text-white" : "bg-mint text-[hsl(var(--vc-ink))]"}`}>
                      {LANG_LABELS[l]}
                    </button>
                  ))}
                </div>
                <Button asChild className="mt-3 bg-brand font-bold text-white">
                  <a href={waLink()} target="_blank" rel="noopener noreferrer">
                    <MessageCircle className="mr-2 h-4 w-4" /> {CONTACT.whatsappDisplay}
                  </a>
                </Button>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;
